
import { defineComponent, ref, onMounted } from 'vue'
import { buildDefaultRequestErrorHandler } from '@/repositories/errorHandler'
import ModalBase from '@/views/modal/ModalBase.vue'
import { repositories } from '@/repositories/system/repositories'
import { object } from 'yup'
import { useForm } from 'vee-validate'
import { myValidationSchemaObj } from '@/views/validation'
import { Company } from '@/repositories/system/data'

const repository = repositories.company

interface InputForm {
  companyName: string;
  isAvailable: boolean;
}

const validationSchema = object({
  companyName: myValidationSchemaObj.maxString(30)
})

export default defineComponent({
  components: {
    ModalBase
  },
  props: {
    isAdd: {
      type: Boolean,
      required: true
    },
    companyKey: {
      type: String,
      required: false
    }
  },
  emits: {
    dismiss: () => true,
    updated: () => true
  },
  setup (props, { emit }) {
    const requestError = ref<string | null>(null)

    const { errors, values, meta, validate } = useForm<InputForm>({
      validationSchema: validationSchema,
      validateOnMount: false
    })

    onMounted(async () => {
      if (props.isAdd) {
        return
      }
      if (!props.companyKey) {
        throw new Error('require tenant key.')
      }
      const company = await repository.get(props.companyKey)
      values.companyName = company.name
      values.isAvailable = company.isAvailable
      await validate()
    })

    const update = async (action: () => Promise<Company>) => {
      requestError.value = null
      try {
        await action()
        emit('updated')
      } catch (e) {
        buildDefaultRequestErrorHandler(requestError, e)
          .handle()
      }
    }

    const onClickedRegister = () => {
      update(() => {
        const param = {
          name: values.companyName,
          isAvailable: values.isAvailable
        }
        return repository.add(param)
      })
    }

    const onClickedModify = () => {
      update(() => {
        const param = {
          name: values.companyName,
          isAvailable: values.isAvailable
        }
        if (props.companyKey) {
          return repository.modify(props.companyKey, param)
        }
        throw new Error('require tenant key.')
      })
    }

    const dismiss = () => {
      emit('dismiss')
    }

    return {
      errors,
      values,
      meta,
      validate,
      requestError,
      onClickedRegister,
      onClickedModify,
      dismiss
    }
  }
})
